import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';

import { Alert, Breadcrumb } from '@shared/components/bootstrap';

import { DiscountSelector } from '@admin/components/discount_selector/discount_selector';
import { Selector as OrderSelector } from '@admin/components/orders/selector';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import { Status, useReferralCreateMutation } from '@admin/schema';
import { IDiscount } from '@admin/types/discount';

import { AccountFormControl } from './account_form_control';

interface SelectedAccount {
  id: string;
  number: string;
  referable?: boolean;
  customer: {
    id: string;
    name: string;
    email?: string | null;
  };
}

const REFERRALS_URL = '/marketing/referrals';

export const CreateReferralForm: React.FC = () => {
  useTitle('New Referral');
  const [referrer, setReferrer] = useState<IDiscount | null>(null);
  const [account, setAccount] = useState<SelectedAccount | null>(null);
  const [orderID, setOrderID] = useState<string | undefined>(undefined);
  const [applyReferralCoupon, setApplyReferralCoupon] = useState(false);
  const [creditReferrer, setCreditReferrer] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const [execute, { loading }] = useReferralCreateMutation({
    client,
    onCompleted: ({ referralCreate }) => {
      if (referralCreate.status === Status.Ok) {
        window.location.href = REFERRALS_URL;
      } else {
        setError(referralCreate.error ?? 'Unable to create referral.');
      }
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  const onDiscountSelected = (discount: IDiscount | null) => {
    setReferrer(discount);
  };

  const onAccountSelected = (selectedAccount: SelectedAccount) => {
    setAccount(selectedAccount);
    setOrderID(undefined);
    if (selectedAccount.referable === false) {
      setCreditReferrer(false);
    }
  };

  const onOrderSelected = (selectedOrderID?: string) => {
    setOrderID(selectedOrderID);
  };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!referrer?.promo_code || !account?.id || !orderID) {
      return;
    }

    setError(undefined);
    execute({
      variables: {
        accountID: account.id,
        applyReferralCoupon,
        creditReferrer,
        orderID,
        promoCode: referrer.promo_code,
      },
    });
  };

  const isAccountNotReferable = account?.referable === false;
  const canSubmit = referrer && account && orderID && !loading;

  return (
    <>
      <h1 className="page-header text-overflow">
        <Breadcrumb>
          <Breadcrumb.Item>
            <NavLink to={REFERRALS_URL}>Referrals</NavLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item active>New</Breadcrumb.Item>
        </Breadcrumb>
      </h1>

      <div className="panel referral-form-panel">
        <form onSubmit={onSubmit} className="create-referral-form">
          <div className="container">
            {error && <Alert style="danger">{error}</Alert>}

            <label htmlFor="referrer">Referrer:</label>
            <div className="row">
              <div className="col-sm-4">
                <DiscountSelector
                  id="referrer"
                  onDiscountSelected={onDiscountSelected}
                  vouchers={false}
                  coupons={false}
                />
              </div>
              <div className="col-sm-8">
                {referrer && (
                  <div style={{ lineHeight: '34px' }}>
                    <strong>{referrer.name}</strong>
                    {referrer.promo_type && ` - ${referrer.promo_type}`}
                  </div>
                )}
              </div>
              <div className="checkbox col-sm-12 text-left">
                <div>
                  <label>
                    <input
                      disabled={isAccountNotReferable}
                      type="checkbox"
                      checked={creditReferrer}
                      onChange={(e) => setCreditReferrer(e.target.checked)}
                    />{' '}
                    Credit {referrer?.name || 'Referrer'}
                  </label>
                  {isAccountNotReferable && (
                    <span className="text-danger">
                      {' '}
                      <strong>Cannot apply credit before the account is activated.</strong>
                    </span>
                  )}
                </div>
              </div>
            </div>

            <label htmlFor="referable">Account:</label>
            <div className="row">
              <div className="col-sm-4">
                <AccountFormControl id="referable" onSelect={onAccountSelected} />
              </div>
              <div className="col-sm-8">
                {account && (
                  <div style={{ lineHeight: '34px' }}>
                    <strong>{account.customer.name}</strong> - {account.number}
                  </div>
                )}
              </div>
              <div className="checkbox col-sm-12 text-left">
                <div>
                  <label>
                    <input
                      type="checkbox"
                      checked={applyReferralCoupon}
                      onChange={(e) => setApplyReferralCoupon(e.target.checked)}
                    />{' '}
                    Apply referral coupon to {account?.customer.name || 'account'}
                  </label>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-4">
                <div className="form-group">
                  <label className="control-label">Order:</label>
                  <OrderSelector
                    name="order_id"
                    accountID={account?.id || ''}
                    selectedID={orderID}
                    onSelect={onOrderSelected}
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-12">
                <button className="btn btn-primary" disabled={!canSubmit} type="submit">
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};
